import {
  ContactLensType,
  FrameMaterial,
  FrameRimType,
  FrameShape,
  LensMaterial,
  LensType,
  PrescriptionLensSupplyMode,
  PRODUCT_TYPE_DETAIL,
  ProductType,
  ReplacementSchedule,
  TargetGroup,
  type ProductVariantSummary,
} from '@optiflow/types';
import type { ApiErrorDetail } from '@optiflow/types';
import { minorToInput, parseMoneyToMinor } from './money';

/**
 * Variant form state.
 *
 * Every field is a string because that is what an `<input>` holds. Conversion to the typed shapes
 * the API expects happens once, in `buildVariantPayload`, so no component deals in half-parsed
 * values.
 */
export interface VariantFormValues {
  sku: string;
  variantName: string;
  costPrice: string;
  sellingPrice: string;
  barcode: string;
  details: Record<string, string>;
}

export const EMPTY_VARIANT_FORM: VariantFormValues = {
  sku: '',
  variantName: '',
  costPrice: '',
  sellingPrice: '',
  barcode: '',
  details: {},
};

export type VariantFormErrors = Partial<
  Record<'sku' | 'sellingPrice' | 'costPrice' | 'barcode', string>
> & {
  details?: Record<string, string>;
};

export interface DetailFieldSpec {
  key: string;
  labelKey: string;
  kind: 'text' | 'integer' | 'decimal' | 'enum' | 'boolean';
  options?: readonly string[];
  /** Enum translation namespace, e.g. `catalog.frameShape`. */
  optionLabelPrefix?: string;
  required?: boolean;
  min?: number;
  max?: number;
  /** Optical powers must land on a quarter dioptre. */
  quarterStep?: boolean;
  placeholder?: string;
  /** Where the field sits: the main grid, the properties checkboxes, or the collapsed advanced section. Checkboxes default to properties, everything else to main. */
  group?: 'main' | 'features' | 'advanced';
  /** Shown only for these product types (or when a value is already recorded); absent means every type that uses the detail block. */
  visibleFor?: readonly ProductType[];
  /** An enum that always has a value, so the select offers no "not specified" choice. */
  noEmptyOption?: boolean;
  /** Pre-filled on a new variant. */
  defaultValue?: string;
}

const FRAME_FIELDS: DetailFieldSpec[] = [
  { key: 'colorName', labelKey: 'catalog.fields.colorName', kind: 'text' },
  { key: 'colorCode', labelKey: 'catalog.fields.colorCode', kind: 'text' },
  { key: 'lensWidthMm', labelKey: 'catalog.fields.lensWidthMm', kind: 'integer', min: 30, max: 80 },
  {
    key: 'bridgeWidthMm',
    labelKey: 'catalog.fields.bridgeWidthMm',
    kind: 'integer',
    min: 10,
    max: 35,
  },
  {
    key: 'templeLengthMm',
    labelKey: 'catalog.fields.templeLengthMm',
    kind: 'integer',
    min: 100,
    max: 200,
  },
  {
    key: 'shape',
    labelKey: 'catalog.fields.shape',
    kind: 'enum',
    options: Object.values(FrameShape),
    optionLabelPrefix: 'catalog.frameShape',
  },
  {
    key: 'material',
    labelKey: 'catalog.fields.material',
    kind: 'enum',
    options: Object.values(FrameMaterial),
    optionLabelPrefix: 'catalog.frameMaterial',
  },
  {
    key: 'rimType',
    labelKey: 'catalog.fields.rimType',
    kind: 'enum',
    options: Object.values(FrameRimType),
    optionLabelPrefix: 'catalog.rimType',
  },
  {
    key: 'targetGroup',
    labelKey: 'catalog.fields.targetGroup',
    kind: 'enum',
    options: Object.values(TargetGroup),
    optionLabelPrefix: 'catalog.targetGroup',
  },
  {
    key: 'isPolarized',
    labelKey: 'catalog.fields.isPolarized',
    kind: 'boolean',
    visibleFor: [ProductType.SUNGLASSES],
  },
  {
    key: 'lensColor',
    labelKey: 'catalog.fields.lensColor',
    kind: 'text',
    visibleFor: [ProductType.SUNGLASSES],
  },
];

const PRESCRIPTION_LENS_FIELDS: DetailFieldSpec[] = [
  {
    key: 'lensType',
    labelKey: 'catalog.fields.lensType',
    kind: 'enum',
    options: Object.values(LensType),
    optionLabelPrefix: 'catalog.lensType',
    required: true,
  },
  {
    key: 'material',
    labelKey: 'catalog.fields.material',
    kind: 'enum',
    options: Object.values(LensMaterial),
    optionLabelPrefix: 'catalog.lensMaterial',
    required: true,
  },
  {
    key: 'supplyMode',
    labelKey: 'catalog.fields.supplyMode',
    kind: 'enum',
    options: Object.values(PrescriptionLensSupplyMode),
    optionLabelPrefix: 'catalog.supplyMode',
    required: true,
    noEmptyOption: true,
    defaultValue: PrescriptionLensSupplyMode.STOCKED,
  },
  {
    key: 'refractiveIndex',
    labelKey: 'catalog.fields.refractiveIndex',
    kind: 'decimal',
    min: 1.4,
    max: 2,
  },
  {
    key: 'sphereMin',
    labelKey: 'catalog.fields.sphereMin',
    kind: 'decimal',
    group: 'advanced',
    min: -30,
    max: 30,
    quarterStep: true,
  },
  {
    key: 'sphereMax',
    labelKey: 'catalog.fields.sphereMax',
    kind: 'decimal',
    group: 'advanced',
    min: -30,
    max: 30,
    quarterStep: true,
  },
  {
    key: 'cylinderMin',
    labelKey: 'catalog.fields.cylinderMin',
    kind: 'decimal',
    group: 'advanced',
    min: -10,
    max: 10,
    quarterStep: true,
  },
  {
    key: 'cylinderMax',
    labelKey: 'catalog.fields.cylinderMax',
    kind: 'decimal',
    group: 'advanced',
    min: -10,
    max: 10,
    quarterStep: true,
  },
  {
    key: 'additionMin',
    labelKey: 'catalog.fields.additionMin',
    kind: 'decimal',
    group: 'advanced',
    min: 0,
    max: 5,
    quarterStep: true,
  },
  {
    key: 'additionMax',
    labelKey: 'catalog.fields.additionMax',
    kind: 'decimal',
    group: 'advanced',
    min: 0,
    max: 5,
    quarterStep: true,
  },
  {
    key: 'diameterMm',
    labelKey: 'catalog.fields.diameterMm',
    kind: 'integer',
    min: 50,
    max: 85,
    group: 'advanced',
  },
  { key: 'hasAntiReflective', labelKey: 'catalog.fields.hasAntiReflective', kind: 'boolean' },
  { key: 'hasBlueLightFilter', labelKey: 'catalog.fields.hasBlueLightFilter', kind: 'boolean' },
  { key: 'isPhotochromic', labelKey: 'catalog.fields.isPhotochromic', kind: 'boolean' },
  { key: 'hasScratchResistant', labelKey: 'catalog.fields.hasScratchResistant', kind: 'boolean' },
];

const CONTACT_LENS_FIELDS: DetailFieldSpec[] = [
  {
    key: 'lensType',
    labelKey: 'catalog.fields.lensType',
    kind: 'enum',
    options: Object.values(ContactLensType),
    optionLabelPrefix: 'catalog.contactLensType',
    required: true,
  },
  {
    key: 'replacement',
    labelKey: 'catalog.fields.replacement',
    kind: 'enum',
    options: Object.values(ReplacementSchedule),
    optionLabelPrefix: 'catalog.replacement',
    required: true,
  },
  { key: 'baseCurve', labelKey: 'catalog.fields.baseCurve', kind: 'decimal', min: 7.5, max: 10 },
  { key: 'diameterMm', labelKey: 'catalog.fields.diameterMm', kind: 'decimal', min: 12, max: 16 },
  {
    key: 'power',
    labelKey: 'catalog.fields.power',
    kind: 'decimal',
    min: -30,
    max: 30,
    quarterStep: true,
  },
  {
    key: 'cylinder',
    labelKey: 'catalog.fields.cylinder',
    kind: 'decimal',
    min: -10,
    max: 10,
    quarterStep: true,
  },
  { key: 'axis', labelKey: 'catalog.fields.axis', kind: 'integer', min: 0, max: 180 },
  {
    key: 'addition',
    labelKey: 'catalog.fields.addition',
    kind: 'decimal',
    min: 0,
    max: 5,
    quarterStep: true,
  },
  { key: 'colorName', labelKey: 'catalog.fields.colorName', kind: 'text' },
  { key: 'packSize', labelKey: 'catalog.fields.packSize', kind: 'integer', min: 1, max: 200 },
  {
    key: 'waterContentPercent',
    labelKey: 'catalog.fields.waterContentPercent',
    kind: 'integer',
    min: 0,
    max: 100,
  },
];

const SOLUTION_FIELDS: DetailFieldSpec[] = [
  {
    key: 'volumeMl',
    labelKey: 'catalog.fields.volumeMl',
    kind: 'integer',
    min: 1,
    max: 5000,
    required: true,
  },
  { key: 'solutionType', labelKey: 'catalog.fields.solutionType', kind: 'text' },
];

const FIELDS_BY_DETAIL: Record<string, DetailFieldSpec[]> = {
  frame: FRAME_FIELDS,
  prescriptionLens: PRESCRIPTION_LENS_FIELDS,
  contactLens: CONTACT_LENS_FIELDS,
  solution: SOLUTION_FIELDS,
};

/** Which detail key a product type uses, and which fields belong to it. */
export function detailKeyFor(type: ProductType): string | null {
  return PRODUCT_TYPE_DETAIL[type];
}

export function detailFieldsFor(type: ProductType): DetailFieldSpec[] {
  const key = detailKeyFor(type);
  return key ? (FIELDS_BY_DETAIL[key] ?? []) : [];
}

/** The group a field is drawn in. */
export function fieldGroup(field: DetailFieldSpec): 'main' | 'features' | 'advanced' {
  return field.group ?? (field.kind === 'boolean' ? 'features' : 'main');
}

/** True when the form holds a recorded value for the field, so hiding it would hide data. */
function hasRecordedValue(field: DetailFieldSpec, raw: string | undefined): boolean {
  const value = raw?.trim() ?? '';
  return field.kind === 'boolean' ? value === 'true' : value !== '';
}

/** The fields a type shows. A field limited to other types stays visible when a value is already recorded, so nothing on file becomes invisible; hidden fields keep their values and are still sent. */
export function visibleDetailFields(
  type: ProductType,
  values: Record<string, string>,
): DetailFieldSpec[] {
  return detailFieldsFor(type).filter(
    (field) =>
      !field.visibleFor ||
      field.visibleFor.includes(type) ||
      hasRecordedValue(field, values[field.key]),
  );
}

/** A blank variant form for a type, with that type's defaults filled in. */
export function emptyVariantFormFor(type: ProductType): VariantFormValues {
  return { ...EMPTY_VARIANT_FORM, details: defaultDetailsFor(type) };
}

/** The detail values a new variant of this type starts with. */
export function defaultDetailsFor(type: ProductType): Record<string, string> {
  const details: Record<string, string> = {};
  for (const field of detailFieldsFor(type)) {
    if (field.defaultValue !== undefined) details[field.key] = field.defaultValue;
  }
  return details;
}

/** Seeds the form from an existing variant, for editing. */
export function toVariantFormValues(
  variant: ProductVariantSummary,
  type: ProductType,
  currencyCode: string,
): VariantFormValues {
  const key = detailKeyFor(type);
  const source = key
    ? ((variant as unknown as Record<string, unknown>)[key] as Record<string, unknown> | null)
    : null;

  const details: Record<string, string> = {};
  if (source) {
    for (const field of detailFieldsFor(type)) {
      const value = source[field.key];
      if (value === null || value === undefined) continue;
      details[field.key] = typeof value === 'boolean' ? String(value) : String(value);
    }
  }

  return {
    sku: variant.sku,
    variantName: variant.variantName ?? '',
    costPrice: minorToInput(variant.costPriceMinor, currencyCode),
    sellingPrice: minorToInput(variant.sellingPriceMinor, currencyCode),
    barcode: '',
    details,
  };
}

function isQuarterStep(value: number): boolean {
  return Number.isInteger(Math.round(value * 100) / 25);
}

/**
 * Validates the form against the same rules the API enforces.
 *
 * A usability affordance, not a security boundary — the server checks every field again, and its
 * answer is the one that counts.
 */
export function validateVariantForm(
  values: VariantFormValues,
  type: ProductType,
  t: (key: string) => string,
  currencyCode: string,
): VariantFormErrors {
  const errors: VariantFormErrors = {};
  const detailErrors: Record<string, string> = {};

  if (parseMoneyToMinor(values.sellingPrice, currencyCode) === null) {
    errors.sellingPrice = t('catalog.validation.priceRequired');
  }
  if (values.costPrice.trim() && parseMoneyToMinor(values.costPrice, currencyCode) === null) {
    errors.costPrice = t('catalog.validation.priceInvalid');
  }
  if (values.sku.trim() && !/^[A-Za-z0-9][A-Za-z0-9._-]*$/.test(values.sku.trim())) {
    errors.sku = t('catalog.validation.skuInvalid');
  }
  if (values.barcode.trim() && values.barcode.trim().length < 4) {
    errors.barcode = t('catalog.validation.barcodeTooShort');
  }

  for (const field of detailFieldsFor(type)) {
    const raw = values.details[field.key]?.trim() ?? '';

    if (field.required && raw === '') {
      detailErrors[field.key] = t('catalog.validation.required');
      continue;
    }
    if (raw === '' || field.kind === 'text' || field.kind === 'enum' || field.kind === 'boolean') {
      continue;
    }

    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) {
      detailErrors[field.key] = t('catalog.validation.numberInvalid');
      continue;
    }
    if (field.kind === 'integer' && !Number.isInteger(parsed)) {
      detailErrors[field.key] = t('catalog.validation.wholeNumber');
      continue;
    }
    if (field.min !== undefined && parsed < field.min) {
      detailErrors[field.key] = t('catalog.validation.outOfRange');
      continue;
    }
    if (field.max !== undefined && parsed > field.max) {
      detailErrors[field.key] = t('catalog.validation.outOfRange');
      continue;
    }
    if (field.quarterStep && !isQuarterStep(parsed)) {
      detailErrors[field.key] = t('catalog.validation.quarterStep');
    }
  }

  if (Object.keys(detailErrors).length > 0) errors.details = detailErrors;

  return errors;
}

export function hasVariantErrors(errors: VariantFormErrors): boolean {
  return Object.entries(errors).some(([key, value]) =>
    key === 'details' ? Object.keys(value ?? {}).length > 0 : Boolean(value),
  );
}

/** Converts form strings into the typed payload the API expects. */
export function buildVariantPayload(
  values: VariantFormValues,
  type: ProductType,
  currencyCode: string,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    sellingPriceMinor: parseMoneyToMinor(values.sellingPrice, currencyCode) ?? 0,
  };

  const cost = parseMoneyToMinor(values.costPrice, currencyCode);
  if (cost !== null) payload.costPriceMinor = cost;
  if (values.sku.trim()) payload.sku = values.sku.trim().toUpperCase();
  if (values.variantName.trim()) payload.variantName = values.variantName.trim();
  if (values.barcode.trim()) payload.barcode = values.barcode.trim();

  const key = detailKeyFor(type);
  if (!key) return payload;

  const details: Record<string, unknown> = {};
  for (const field of detailFieldsFor(type)) {
    const raw = values.details[field.key]?.trim() ?? '';

    // A checkbox the form holds a value for is sent either way, so unticking one on an existing variant clears it; one never touched on a new form is left out.
    if (field.kind === 'boolean') {
      if (raw !== '') details[field.key] = raw === 'true';
      continue;
    }
    if (raw === '') continue;

    details[field.key] = field.kind === 'integer' || field.kind === 'decimal' ? Number(raw) : raw;
  }

  if (Object.keys(details).length > 0) payload[key] = details;

  return payload;
}

/** Why the server refused a supply-mode change, read from its field details: stock history, or a purchase order still owed. Null for any other refusal. */
export function supplyModeRefusal(
  details: readonly ApiErrorDetail[],
): { kind: 'stockHistory' } | { kind: 'openOrder'; reference: string } | null {
  const mine = details.filter((detail) => detail.field === 'prescriptionLens.supplyMode');
  const order = mine.find((detail) => detail.message.startsWith('on open purchase order '));
  if (order)
    return { kind: 'openOrder', reference: order.message.slice('on open purchase order '.length) };
  return mine.some((detail) => detail.message.endsWith(' exist')) ? { kind: 'stockHistory' } : null;
}
